import React, { useState, useEffect, useRef } from 'react';
import Icon from '@mdi/react';
import {
  mdiShareVariant,
  mdiContentCopy,
  mdiSend,
  mdiClose
} from '@mdi/js';

const ShareButton = ({
  layerSettings = [],
  formattedDate = '',
  personalSettings = {},
  isSquare = false,
  otherSettings = {},
  frameSettings = {},
  frameOptions = {},
  exportSVGString = null,
  apiBase = '/wp-json/rotateadate/v1',
  siteBase = null,
}) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shareId, setShareId] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const dialogRef = useRef(null);

  const getNonce = () =>
    (window.RAD_CONFIG && window.RAD_CONFIG.wpNonce) ||
    (window.gelatoConnect && window.gelatoConnect.nonce) ||
    (window.wpApiSettings && window.wpApiSettings.nonce) ||
    null;

  const buildPayload = async () => {
    return {
      layerSettings,
      formattedDate,
      personalSettings,
      isSquare,
      otherSettings,
      frameSettings,
      frameOptions,
      createdAt: new Date().toISOString(),
    };
  };

  const createOrRefreshShare = async () => {
    setError(null);
    setSaving(true);
    setCopied(false);
    setShareId(null);
    setShareUrl('');
    setEmailStatus(null);

    try {
      const payload = await buildPayload();
      if (!payload) throw new Error('Empty payload');

      const nonce = getNonce();

      const res = await fetch(`${apiBase}/save-graphic`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
        },
        body: JSON.stringify({ data: payload }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Save failed: ${res.status}${t ? ` — ${t}` : ''}`);
      }

      const json = await res.json();
      const id = json.id ?? json.data?.id;
      if (!id) throw new Error('No ID returned');

      const base = siteBase || window.location.origin;
      const url = `${base}/create/?graphicid=${id}`;

      setShareId(id);
      setShareUrl(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openDialog = async () => {
    setOpen(true);
    await createOrRefreshShare();
  };

  const closeDialog = () => {
    setOpen(false);
    setCopied(false);
    setEmail('');
    setEmailStatus(null);
    setError(null);
  };

  const copyToClipboard = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      alert('Copy failed — please copy manually.');
    }
  };

  const sendEmail = async () => {
    if (!email || !shareUrl) {
      setEmailStatus({ ok: false, msg: 'Enter an email first.' });
      return;
    }

    setSendingEmail(true);
    setEmailStatus(null);

    try {
      const nonce = getNonce();
      const payload = { id: shareId, email, link: shareUrl };

      const res = await fetch(`${apiBase}/send-share-email`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setEmailStatus({ ok: true, msg: 'Email sent successfully.' });
      } else {
        // fallback to mailto
        const subject = encodeURIComponent('Shared RotateADate Design');
        const body = encodeURIComponent(`Here is your shared design link:\n${shareUrl}`);
        window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;

        setEmailStatus({ ok: true, msg: 'Opened mail client.' });
      }
    } catch (err) {
      // fallback mailto
      const subject = encodeURIComponent('Shared RotateADate Design');
      const body = encodeURIComponent(`Here is your shared design link:\n${shareUrl}`);
      window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
      setEmailStatus({ ok: true, msg: 'Opened mail client.' });
    }

    setSendingEmail(false);
  };

  // Close dialog when clicking outside
  useEffect(() => {
    const listener = (ev) => {
      if (!open) return;
      if (dialogRef.current && !dialogRef.current.contains(ev.target) && !ev.target.closest('.share-icon-button')) {
        closeDialog();
      }
    };

    document.addEventListener('pointerdown', listener);
    return () => document.removeEventListener('pointerdown', listener);
  }, [open]);

  return (
    <>
      {/* Share icon */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          className="share-icon-button"
          onClick={open ? closeDialog : openDialog}
          title="Share design"
          style={{
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <Icon path={mdiShareVariant} color="#fff" size={'20px'} />
        </button>
      </div>

      {/* Dialog */}
      {open && (
        <div
          ref={dialogRef}
          style={{
            position: 'absolute',
            right:64,
            top: -100,
            zIndex: 2000,
            width: 360,
            background: '#0f1724',
            color: '#fff',
            borderRadius: 8,
            padding: 14,
            boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong style={{ fontSize: 15 }}>Share Link</strong>

            <button
              onClick={closeDialog}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#cbd5e1',
                cursor: 'pointer',
              }}
            >
              <Icon path={mdiClose} size={0.9} />
            </button>
          </div>

          {/* Link area */}
          <div>
            <div style={{ marginBottom: 6, fontSize: 12, color: '#94a3b8' }}>Generated Link</div>
            <div style={{ position: 'absolute', right: 18, top: 60 }}>
                {copied && <div style={{ marginTop: 6, fontSize: 12, color: '#34D399' }}>Copied!</div>}
                {error && <div style={{ marginTop: 6, fontSize: 12, color: '#f87171' }}>{error}</div>}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: '#2c2c2cff',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: 6,
                padding: '8px 10px',
                wordBreak: 'break-all',
              }}
            >
              <div style={{ flex: 1, fontSize: 13, overflow: 'hidden', height: 20 }}>
                {saving ? 'Generating…' : (shareUrl || 'https://rotateadate.com/create?graphicid=') + shareUrl}
              </div>

              <button
                onClick={copyToClipboard}
                disabled={saving || !shareUrl}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 6,
                  color: copied ? '#34D399' : '#fff',
                }}
              >
                <Icon path={mdiContentCopy} size={0.8} />
              </button>
            </div>

          </div>

          {/* Email section */}
<div style={{ marginTop: 18 }}>
  <div style={{ marginBottom: 6, fontSize: 12, color: '#94a3b8' }}>Send by Email</div>

  {/* SUCCESS MESSAGE */}
  {emailStatus && emailStatus.ok ? (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 6,
        background: 'rgba(52,211,153,0.08)',
        border: '1px solid rgba(52,211,153,0.18)',
        color: '#34D399',
        fontSize: 14,
      }}
    >
      {emailStatus.msg || 'Email sent successfully!'}
    </div>
  ) : (
    <>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="email"
          placeholder="email@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // *** DO NOT CLEAR ERROR HERE IF BLANK ***
            if (emailStatus && emailStatus.ok === false) {
              setEmailStatus(null);
            }
          }}
          style={{
            flex: 1,
            padding: '6px 8px',
            borderRadius: 4,
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
          }}
        />

        <button
          onClick={() => {
            const trimmed = email.trim();

            // --- VALIDATION 1: empty field ---
            if (!trimmed) {
              setEmailStatus({
                ok: false,
                msg: 'Please enter an email address.',
              });
              return; // STOP HERE
            }

            // --- VALIDATION 2: format check ---
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            if (!emailRegex.test(trimmed)) {
              setEmailStatus({
                ok: false,
                msg: 'Enter a valid email address.',
              });
              return; // STOP HERE
            }

            // --- VALIDATION PASSED: send email ---
            sendEmail();
          }}
          disabled={!shareUrl || sendingEmail}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: sendingEmail ? '#334155' : '#fff',
            border: 'none',
            padding: '8px 12px',
            borderRadius: 6,
            cursor: sendingEmail ? 'wait' : 'pointer',
            color: '#0f172a',
            fontWeight: 600,
          }}
        >
          <Icon path={mdiSend} size={0.8} />
          <span>{sendingEmail ? 'Sending…' : 'Send'}</span>
        </button>
      </div>

      {/* ERROR MESSAGE */}
      {emailStatus && emailStatus.ok === false && (
        <div style={{ fontSize: 12, color: '#f87171',  position: 'absolute', right: 5 , top: 173 }}>
          {emailStatus.msg}
        </div>
      )}
    </>
  )}
</div>

          
        </div>
      )}
    </>
  );
};

export default ShareButton;
